import ActionDialog from "@dashboard/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import {
  NewRadioGroupField as RadioGroupField,
  RadioGroupFieldChoice,
} from "@dashboard/components/RadioGroupField";
import { OrderErrorFragment, OrderPaymentReminderModeEnum } from "@dashboard/graphql";
import useModalDialogErrors from "@dashboard/hooks/useModalDialogErrors";
import getOrderErrorMessage from "@dashboard/utils/errors/order";
import { Box, Text } from "@saleor/macaw-ui-next";
import { FormattedMessage, useIntl } from "react-intl";

interface OrderPaymentReminderDialogProps {
  confirmButtonState: ConfirmButtonTransitionState;
  errors: OrderErrorFragment[];
  open: boolean;
  mode: OrderPaymentReminderModeEnum;
  onClose: () => void;
  onConfirm: () => void;
  onModeChange: (mode: OrderPaymentReminderModeEnum) => void;
}

const OrderPaymentReminderDialog = ({
  confirmButtonState,
  errors: apiErrors,
  open,
  mode,
  onClose,
  onConfirm,
  onModeChange,
}: OrderPaymentReminderDialogProps) => {
  const intl = useIntl();
  const errors = useModalDialogErrors(apiErrors, open);

  const choices: Array<RadioGroupFieldChoice<OrderPaymentReminderModeEnum>> = [
    {
      value: OrderPaymentReminderModeEnum.PAYMENT_LINK,
      label: intl.formatMessage({
        id: "j7Xijg",
        defaultMessage: "Include a payment link",
        description: "payment reminder mode, radio choice",
      }),
    },
    {
      value: OrderPaymentReminderModeEnum.EXPLICIT_PAYMENT_DATA,
      label: intl.formatMessage({
        id: "yo1USp",
        defaultMessage: "Include the payment details (e.g. bank transfer)",
        description: "payment reminder mode, radio choice",
      }),
    },
  ];

  return (
    <ActionDialog
      confirmButtonState={confirmButtonState}
      open={open}
      title={intl.formatMessage({
        id: "CoYXZa",
        defaultMessage: "Send payment reminder",
        description: "dialog header",
      })}
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <Box display="grid" gap={4}>
        <Text>
          <FormattedMessage
            id="fQINve"
            defaultMessage="This order is still unpaid. Choose how the reminder email should help the customer pay:"
          />
        </Text>
        <RadioGroupField
          name="paymentReminderMode"
          value={mode}
          choices={choices}
          onChange={event => onModeChange(event.target.value as OrderPaymentReminderModeEnum)}
        />
        {errors.length > 0 && (
          <Box display="grid" gap={1}>
            {errors.map((err, index) => (
              <Text color="critical1" key={index}>
                {getOrderErrorMessage(err, intl)}
              </Text>
            ))}
          </Box>
        )}
      </Box>
    </ActionDialog>
  );
};

OrderPaymentReminderDialog.displayName = "OrderPaymentReminderDialog";
export default OrderPaymentReminderDialog;
